import type { ReactNode } from 'react'

export type ContentTabId =
  | 'guia'
  | 'colaborador'
  | 'gestor'
  | 'biblioteca'
  | 'videos'
  | 'acessos'
  | 'beneficios'

type ContentTab = {
  id: ContentTabId
  label: string
  title: string
  render: () => ReactNode
}

function InfoGrid({ items }: { items: Array<{ title: string; text: string }> }) {
  return (
    <div className="hi-info-grid">
      {items.map((it) => (
        <article key={it.title} className="hi-info-card">
          <h5>{it.title}</h5>
          <p>{it.text}</p>
        </article>
      ))}
    </div>
  )
}

function Faq({ items }: { items: Array<{ q: string; a: string }> }) {
  return (
    <div className="hi-faq">
      {items.map((f) => (
        <details key={f.q}>
          <summary>{f.q}</summary>
          <p>{f.a}</p>
        </details>
      ))}
    </div>
  )
}

function CheckList({ items }: { items: string[] }) {
  return (
    <ul className="hi-checklist hi-checklist--static">
      {items.map((t) => (
        <li key={t}>
          <label className="hi-check">
            <input type="checkbox" disabled />
            <span>{t}</span>
          </label>
        </li>
      ))}
    </ul>
  )
}

const CATEGORIAS = [
  { id: 'cultura', label: 'Cultura Organizacional' },
  { id: 'beneficios', label: 'Benefícios' },
  { id: 'seguranca', label: 'Segurança' },
  { id: 'ferramentas', label: 'Ferramentas' },
  { id: 'treinamentos', label: 'Treinamentos' },
  { id: 'compliance', label: 'Compliance' },
]

const DOCS = [
  { nome: 'Manual do Colaborador', cat: 'Cultura Organizacional', tipo: 'PDF', ver: 'v3.2', quando: 'Jan/2025' },
  { nome: 'Política de Benefícios', cat: 'Benefícios', tipo: 'PDF', ver: 'v2.1', quando: 'Fev/2025' },
  { nome: 'Procedimentos de Segurança', cat: 'Segurança', tipo: 'PDF', ver: 'v4.0', quando: 'Mar/2025' },
  { nome: 'Guia do Microsoft 365', cat: 'Ferramentas', tipo: 'PDF', ver: 'v1.5', quando: 'Dez/2024' },
  { nome: 'Trilha de Onboarding', cat: 'Treinamentos', tipo: 'Vídeo', ver: 'v2.0', quando: 'Jan/2025' },
  { nome: 'Código de Conduta', cat: 'Compliance', tipo: 'PDF', ver: 'v1.8', quando: 'Nov/2024' },
  { nome: 'Política LGPD', cat: 'Compliance', tipo: 'PDF', ver: 'v2.3', quando: 'Mar/2025' },
]

const VIDEOS = [
  { titulo: 'Mensagem do CEO — Boas-vindas', cat: 'Institucional', dur: '5:32' },
  { titulo: 'Conheça a Nossa Empresa', cat: 'Institucional', dur: '12:15' },
  { titulo: 'Nossos Valores e Cultura', cat: 'Cultura', dur: '8:40' },
  { titulo: 'Tour Virtual pelas Instalações', cat: 'Institucional', dur: '15:20' },
  { titulo: 'Como Usar o Portal do RH', cat: 'Ferramentas', dur: '10:05' },
  { titulo: 'Política de Benefícios', cat: 'Benefícios', dur: '7:18' },
]

const SISTEMAS = [
  { label: 'E-mail Corporativo', desc: 'Google Workspace / Microsoft 365' },
  { label: 'ERP', desc: 'Sistema de gestão empresarial' },
  { label: 'Bitrix24', desc: 'CRM e gestão de projetos' },
  { label: 'VPN', desc: 'Acesso remoto seguro' },
  { label: 'Microsoft Teams', desc: 'Comunicação e videoconferência' },
  { label: 'WhatsApp Corporativo', desc: 'Grupos de trabalho' },
  { label: 'Ponto Eletrônico', desc: 'Controle de jornada' },
]

export const CONTENT_TABS: ContentTab[] = [
  {
    id: 'guia',
    label: 'Guia',
    title: 'Guia de Integração',
    render: () => (
      <>
        <div className="hi-info-grid">
          <article className="hi-info-card">
            <h5>PDFs e manuais</h5>
            <ul className="hi-doc-list">
              {[
                'Manual do Colaborador',
                'Política de Benefícios',
                'Código de Conduta',
                'Política de Privacidade (LGPD)',
                'Regimento Interno',
              ].map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          </article>
          <article className="hi-info-card">
            <h5>Links rápidos</h5>
            <ul className="hi-doc-list">
              <li>Portal do Colaborador</li>
              <li>HCOS Dashboard</li>
              <li>
                <a href="https://wellhub.com" target="_blank" rel="noreferrer">
                  Wellhub / Gympass
                </a>
              </li>
              <li>
                <a href="https://conexasaude.com.br" target="_blank" rel="noreferrer">
                  Conexa Saúde
                </a>
              </li>
            </ul>
          </article>
        </div>
        <h5 className="hi-subhead">Checklist modelo de integração</h5>
        <CheckList
          items={[
            'Documentação recebida e assinada',
            'Crachá e uniformes entregues',
            'Acesso ao e-mail corporativo criado',
            'Tour pelas instalações realizado',
            'Apresentado ao time e gestor',
            'Treinamentos obrigatórios iniciados',
            'Benefícios ativados (VA/VR/Saúde)',
            'Avaliação de 45 dias agendada',
          ]}
        />
        <p className="hi-muted">
          O acompanhamento real por pessoa fica na aba Acompanhamento.
        </p>
      </>
    ),
  },
  {
    id: 'colaborador',
    label: 'Colaborador',
    title: 'Guia do Colaborador',
    render: () => (
      <>
        <InfoGrid
          items={[
            {
              title: 'Bem-vindo(a)!',
              text: 'Esta área concentra o essencial para começar com o pé direito.',
            },
            {
              title: 'Primeira semana',
              text: 'Cronograma de integração: treinamentos, reuniões e visitas.',
            },
            {
              title: 'Área de atuação',
              text: 'Processos do setor, gestor e fluxo de trabalho.',
            },
            {
              title: 'Metas e expectativas',
              text: 'Objetivos de curto prazo nos primeiros 90 dias.',
            },
            {
              title: 'Fale conosco',
              text: 'Dúvidas com o RH pelo portal ou WhatsApp corporativo.',
            },
            {
              title: 'Políticas internas',
              text: 'Home office, banco de horas, férias e código de conduta.',
            },
          ]}
        />
        <h5 className="hi-subhead">Perguntas frequentes</h5>
        <Faq
          items={[
            {
              q: 'Como acesso o controle de ponto?',
              a: 'Pelo app corporativo ou pelo portal web de ponto.',
            },
            {
              q: 'Quando recebo o primeiro salário?',
              a: 'No 5º dia útil do mês seguinte (proporcional se admissão no meio do mês).',
            },
            {
              q: 'Como solicito férias?',
              a: 'Portal → Departamento Pessoal → Férias, com aprovação do gestor e RH.',
            },
            {
              q: 'Como acesso meus contracheques?',
              a: 'Portal → DP → Folha de Pagamento, após o fechamento mensal.',
            },
          ]}
        />
      </>
    ),
  },
  {
    id: 'gestor',
    label: 'Gestor',
    title: 'Guia do Gestor',
    render: () => (
      <>
        <InfoGrid
          items={[
            {
              title: 'Responsabilidades',
              text: 'Você é o principal ponto de contato do novo colaborador.',
            },
            {
              title: 'Agenda de integração',
              text: 'Organize a primeira semana: apresentações, sistemas e treinamentos.',
            },
            {
              title: 'Expectativas',
              text: 'Metas claras para 30, 60 e 90 dias alinhadas à experiência.',
            },
            {
              title: 'Acompanhamento',
              text: 'Use a avaliação de experiência nos marcos de 45 e 90 dias.',
            },
            {
              title: 'Acessos',
              text: 'Solicite ao TI e-mail, ERP, VPN e demais sistemas necessários.',
            },
            {
              title: 'Comunicação',
              text: 'Apresente o novo membro ao time no Feed ou e-mail de boas-vindas.',
            },
          ]}
        />
        <h5 className="hi-subhead">Checklist do gestor</h5>
        <CheckList
          items={[
            'Confirmar chegada dos documentos admissionais',
            'Solicitar criação de acessos ao TI',
            'Agendar reunião de boas-vindas com o time',
            'Apresentar o colaborador aos stakeholders',
            'Definir metas e KPIs dos primeiros 90 dias',
            'Agendar 1:1 semanal nas primeiras 8 semanas',
            'Validar a avaliação de 45 dias no sistema',
            'Confirmar ativação de benefícios',
            'Garantir acesso ao controle de ponto',
            'Realizar avaliação final de 90 dias',
          ]}
        />
      </>
    ),
  },
  {
    id: 'biblioteca',
    label: 'Biblioteca',
    title: 'Biblioteca Corporativa',
    render: () => (
      <>
        <div className="hi-info-grid">
          {CATEGORIAS.map((c) => (
            <article key={c.id} className="hi-info-card">
              <h5>{c.label}</h5>
              <p>Documentos, vídeos e links da categoria.</p>
            </article>
          ))}
        </div>
        <div className="tbl-wrapper" style={{ marginTop: 16 }}>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Categoria</th>
                  <th>Tipo</th>
                  <th>Versão</th>
                  <th>Atualizado</th>
                </tr>
              </thead>
              <tbody>
                {DOCS.map((d) => (
                  <tr key={d.nome}>
                    <td>
                      <strong>{d.nome}</strong>
                    </td>
                    <td>{d.cat}</td>
                    <td>{d.tipo}</td>
                    <td>{d.ver}</td>
                    <td>{d.quando}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="hi-muted" style={{ marginTop: 12 }}>
          Upload e edição avançada permanecem no legado até a API de biblioteca.
        </p>
      </>
    ),
  },
  {
    id: 'videos',
    label: 'Vídeos',
    title: 'Vídeos Institucionais',
    render: () => (
      <div className="tbl-wrapper">
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Título</th>
                <th>Categoria</th>
                <th>Duração</th>
              </tr>
            </thead>
            <tbody>
              {VIDEOS.map((v) => (
                <tr key={v.titulo}>
                  <td>
                    <strong>{v.titulo}</strong>
                  </td>
                  <td>{v.cat}</td>
                  <td>{v.dur}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ),
  },
  {
    id: 'acessos',
    label: 'Acessos',
    title: 'Acessos Corporativos',
    render: () => (
      <div className="hi-info-grid">
        {SISTEMAS.map((s) => (
          <article key={s.label} className="hi-info-card">
            <h5>{s.label}</h5>
            <p>{s.desc}</p>
          </article>
        ))}
      </div>
    ),
  },
  {
    id: 'beneficios',
    label: 'Benefícios',
    title: 'Benefícios na integração',
    render: () => (
      <>
        <InfoGrid
          items={[
            {
              title: 'Conexa Saúde',
              text: 'Telemedicina: cadastro com CPF, vínculo com a empresa e primeira consulta.',
            },
            {
              title: 'Wellhub / Gympass',
              text: 'App Wellhub com e-mail corporativo, plano da empresa e check-in.',
            },
          ]}
        />
        <Faq
          items={[
            {
              q: 'Quando posso usar a Conexa?',
              a: 'Imediatamente após a ativação do cadastro.',
            },
            {
              q: 'Tem custo para o colaborador?',
              a: 'Não. O plano é custeado pela empresa.',
            },
            {
              q: 'Quais academias no Wellhub?',
              a: 'Mais de 15.000 academias parceiras, além de modalidades online.',
            },
          ]}
        />
      </>
    ),
  },
]
